import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage, zSubdomain } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { CopyIcon, ExternalLinkIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { FormWrapper } from '@/components/FormWrapper';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { env } from '@/lib/env';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';

type Site = NonNullable<FunctionReturnType<typeof api.sites.get>>;

const formValuesSchema = z.object({ subdomain: zSubdomain });
type FormValues = z.output<typeof formValuesSchema>;

export const Route = createFileRoute('/r/$restaurantId/s/website-address')({
  component: WebsiteAddressPage,
});

/**
 * A venue's site (a Zest site, `mycafe.zestordering.site`): copy or open it, and change its name.
 * Super admins change misleading or offensive names here too.
 */
function WebsiteAddressPage() {
  const { restaurantId } = Route.useParams();
  const { data: site, error, isError, isPending } = useQuery(api.sites.get, { restaurantId });

  if (isPending) return <LoadingIcon />;

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading website address" />;
  }

  if (!site) {
    return (
      <ErrorAlert
        message="Free website addresses come with Zest."
        title="Your venue has no free website address"
      />
    );
  }

  return (
    <>
      <CurrentAddressCard site={site} />
      <ChangeAddressCard site={site} />
    </>
  );
}

const CurrentAddressCard = ({ site }: { site: Site }) => {
  // consumer-web serves the site, so the link takes its scheme and port
  const consumerWebUrl = new URL(env.VITE_CONSUMER_WEB_URL);
  consumerWebUrl.hostname = site.hostname;
  const url = consumerWebUrl.origin;

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Website address</CardTitle>
        <CardDescription>Customers order online here.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        <p className="font-medium break-all">{url}</p>
        {site.customDomain && (
          <p className="text-muted-foreground text-sm">
            Your custom domain is your main address. This one forwards to it.
          </p>
        )}
      </CardContent>
      <CardFooter className="gap-2 border-t">
        <ActionButton onClick={copyLink} variant="outline">
          <CopyIcon />
          Copy link
        </ActionButton>
        <ActionButton asChild variant="outline">
          <a href={url} rel="noreferrer" target="_blank">
            <ExternalLinkIcon />
            Open site
          </a>
        </ActionButton>
      </CardFooter>
    </Card>
  );
};

const ChangeAddressCard = ({ site }: { site: Site }) => {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // e.g. `.zestordering.site`
  const suffix = site.hostname.slice(site.subdomain.length);

  const form = useForm({
    defaultValues: { subdomain: site.subdomain },
    mode: 'onChange',
    resolver: zodResolver(formValuesSchema),
  });

  // Subscribe to changes
  useEffect(() => {
    // Ensure un-saved user changes are kept on Convex sync
    form.reset({ subdomain: site.subdomain }, { keepDirty: true, keepDirtyValues: true });
  }, [form, site.subdomain]);

  // Check the name once typing pauses, and only a valid name that isn't the current one
  const subdomain = useWatch({ control: form.control, name: 'subdomain' });
  const [typedSubdomain, setTypedSubdomain] = useState(subdomain);
  useEffect(() => {
    const timeoutId = setTimeout(() => setTypedSubdomain(subdomain), 300);
    return () => clearTimeout(timeoutId);
  }, [subdomain]);
  const isNewValidName = subdomain !== site.subdomain && zSubdomain.validate(subdomain);
  const { data: isAvailable } = useQuery(
    api.sites.isSubdomainAvailable,
    isNewValidName && typedSubdomain === subdomain
      ? { restaurantId, subdomain: typedSubdomain }
      : 'skip',
  );

  const onValidSubmit = () => setIsConfirmOpen(true);

  const saveSubdomain = async (formValues: FormValues) => {
    setIsSaving(true);
    try {
      await convex.mutation(api.sites.saveSubdomain, {
        restaurantId,
        subdomain: formValues.subdomain,
      });
    } catch (error) {
      toast.error("Couldn't change your website address", {
        description: getUserErrorMessage(error),
      });
      setIsSaving(false);
      return;
    }
    // The saved values are the new baseline, so nothing is dirty any more
    form.reset(formValues);
    setIsSaving(false);
    setIsConfirmOpen(false);
    toast.success('Website address changed');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change your address</CardTitle>
        <CardDescription>Use letters, numbers and hyphens.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="website-address-form"
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
          >
            <FormField
              control={form.control}
              name="subdomain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        autoCapitalize="none"
                        autoComplete="off"
                        className="max-w-xs"
                        onChange={(event) =>
                          field.onChange(event.target.value.toLowerCase().replace(/\s+/g, '-'))
                        }
                        spellCheck={false}
                      />
                    </FormControl>
                    <span className="text-muted-foreground text-sm">{suffix}</span>
                  </div>
                  {isNewValidName && (
                    <FormDescription
                      className={cn(
                        isAvailable === true && 'text-green-600',
                        isAvailable === false && 'text-destructive',
                      )}
                    >
                      {isAvailable === undefined
                        ? 'Checking…'
                        : isAvailable
                          ? 'Available'
                          : 'That address is taken'}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={!form.formState.isDirty || !isNewValidName || isAvailable !== true}
          form="website-address-form"
          type="submit"
        >
          Save
        </ActionButton>
      </CardFooter>
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change your website address?</AlertDialogTitle>
            <AlertDialogDescription>
              Your old address, links and QR codes stop working straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isSaving} onClick={() => saveSubdomain(form.getValues())}>
              Yes
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
